/**
 * Access Key Service - Validates user access keys before file upload
 *
 * Provides simple but secure access control to prevent IPFS/storage abuse.
 * Keys are stored in Airtable with usage tracking and expiration.
 *
 * Key Format: STOP-XXXX-XXXX (easy to read and type)
 * Example: STOP-A3B7-C9D2
 */

class AccessKeyService {
  constructor() {
    // API endpoint for key validation
    this.validationEndpoint = '/api/validate-access-key';

    // Session storage key
    this.storageKey = 'stopvio_access_key';

    console.log('🔑 [AccessKeyService] Initialized');
  }

  /**
   * Validate access key with server
   *
   * @param {string} accessKey - Access key to validate
   * @returns {Promise<{valid: boolean, message?: string, apiKey?: string}>}
   */
  async validateKey(accessKey) {
    try {
      console.log('🔍 [AccessKeyService] Validating key...');

      if (!accessKey || accessKey.trim().length === 0) {
        return {
          valid: false,
          message: 'Please enter an access key'
        };
      }

      // Clean and format key
      const cleanKey = accessKey.trim().toUpperCase();

      // Send to server for validation
      const response = await fetch(this.validationEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accessKey: cleanKey })
      });

      const result = await response.json();

      if (result.valid) {
        // Store in session for convenience
        this.storeKeyInSession(cleanKey);
        console.log('✅ [AccessKeyService] Key validated successfully');
      } else {
        console.warn('⚠️ [AccessKeyService] Invalid key:', result.message);
      }

      return result;

    } catch (error) {
      console.error('❌ [AccessKeyService] Validation failed:', error);
      return {
        valid: false,
        message: 'Unable to validate access key. Please try again.'
      };
    }
  }

  /**
   * Store valid key in session storage
   * Key expires when browser session ends (or tab closes)
   *
   * @param {string} accessKey - Valid access key
   */
  storeKeyInSession(accessKey) {
    try {
      sessionStorage.setItem(this.storageKey, accessKey);
      console.log('💾 [AccessKeyService] Key stored in session');
    } catch (error) {
      console.warn('⚠️ [AccessKeyService] Could not store key:', error);
    }
  }

  /**
   * Get stored key from session
   *
   * @returns {string|null} Stored access key or null
   */
  getStoredKey() {
    try {
      const key = sessionStorage.getItem(this.storageKey);
      if (key) {
        console.log('📖 [AccessKeyService] Retrieved key from session');
      }
      return key;
    } catch (error) {
      console.warn('⚠️ [AccessKeyService] Could not retrieve key:', error);
      return null;
    }
  }

  /**
   * Check if user has a valid key in session
   *
   * @returns {boolean} True if key exists in session
   */
  hasStoredKey() {
    return !!this.getStoredKey();
  }

  /**
   * Clear stored key from session
   */
  clearStoredKey() {
    try {
      sessionStorage.removeItem(this.storageKey);
      console.log('🗑️ [AccessKeyService] Key cleared from session');
    } catch (error) {
      console.warn('⚠️ [AccessKeyService] Could not clear key:', error);
    }
  }

  /**
   * Format access key with dashes
   * STOPXXXX → STOP-XXXX-XXXX
   *
   * @param {string} input - Raw input
   * @returns {string} Formatted key
   */
  formatKey(input) {
    // Remove all non-alphanumeric characters
    const clean = input.replace(/[^A-Z0-9]/gi, '').toUpperCase();

    // Add dashes: STOP-XXXX-XXXX
    if (clean.length <= 4) {
      return clean;
    } else if (clean.length <= 8) {
      return `${clean.slice(0, 4)}-${clean.slice(4)}`;
    } else {
      return `${clean.slice(0, 4)}-${clean.slice(4, 8)}-${clean.slice(8, 12)}`;
    }
  }

  /**
   * Validate key format (client-side)
   *
   * @param {string} key - Access key
   * @returns {boolean} True if format is valid
   */
  isValidFormat(key) {
    const clean = key.replace(/[^A-Z0-9]/gi, '');
    // Expect 12 characters: STOP + 8 random chars
    return clean.length === 12 && clean.startsWith('STOP');
  }
}

// Export singleton instance
const accessKeyService = new AccessKeyService();

export default accessKeyService;
export { AccessKeyService };
